<script>
import YouTubeVideoPlayer from '@rei/youtube-video-player';
import { StoriesComponent, StoryComponent } from '@rei/landing-story';
import { LandingAction, LandingContentBlock } from '@rei/landing-content-block';
import { CdrText, CdrModal, IconPlayFill } from '@rei/cedar';
import LandingBanner from '@rei/banner-component';
import LandingImageComponent from '@rei/landing-image-component';
import LongformComponent from '@rei/landing-longform-component';
import FrequentlyAskedQuestions from '@rei/frequently-asked-questions';
import analytics from '../../common/mixins/analytics';
import LeadComponent from '../../common/components/lead/LeadComponent.vue';
import InPageNav from './components/InPageNav.vue';

import {
  transformPromotionToLandingStories,
} from '../../utils/formatContentStackData';

export default {
  name: 'AboutReiLanding',
  components: {
    LeadComponent,
    StoriesComponent,
    LandingBanner,
    LandingImageComponent,
    InPageNav,
    StoryComponent,
    LandingContentBlock,
    LandingAction,
    YouTubeVideoPlayer,
    CdrModal,
    CdrText,
    LongformComponent,
    IconPlayFill,
    FrequentlyAskedQuestions,
  },
  mixins: [analytics],
  props: {
    componentModels: { type: Object, required: true, default: (() => {}) },
    additionalData: { type: Object, required: true, default: (() => {}) },
  },
  data() {
    return {
      modalOpen: false,
      modalContent: {},
    };
  },
  computed: {
    renderingMap() {
      return {
        lead: !!this.lead,
        'in-page-nav': !!this.inPageNav,
        title: !!this.title,
        banner: !!this.banner,
        longform: !!this.longform,
        'upper-one-up-stories': !!this.upperOneUpStories,
        'video-player': !!this.videoPlayer,
        'two-up-stories': !!this.twoUpStories,
        'three-up-stories': !!this.threeUpStories,
        'lower-one-up-stories': !!this.lowerOneUpStories,
        faq: !!this.faq,
      };
    },
    lead() {
      const { lead } = this.componentModels || {};
      const { media, content, bannerComponentProps } = lead || {};
      const renderComponent = !!lead;
      return (renderComponent && {
        media,
        content,
        bannerComponentProps,
      }) || null;
    },
    videoPlayer() {
      const { videoData } = this.componentModels || {};
      const hasVideo = videoData?.hasVideo ?? false;
      const videoId = videoData?.videoId ?? null;
      const header = videoData?.header ?? {};
      const stories = Array.isArray(header.stories) ? header.stories : [];
      const ambientVideoUrl = videoData?.ambientVideoUrl ?? '';
      const playerButtonLabel = videoData?.playerButtonLabel ?? '';
      const poster = videoData?.poster ?? null;
      const ratio = videoData?.ratio ?? null;
      const reduceMotion = videoData?.reduceMotion ?? null;
      const renderComponent = videoData;
      return (
        (renderComponent && {
          hasVideo,
          ambientVideoUrl,
          header,
          stories,
          playerButtonLabel,
          poster,
          ratio,
          reduceMotion,
          videoId,
        })
          || null
      );
    },
    inPageNav() {
      const { subnav } = this.componentModels || {};

      const renderComponent = !!subnav;
      return (
        (renderComponent && {
          items: subnav.items,
          id: subnav?.id || null,
        })
        || null
      );
    },
    title() {
      const { title } = this.componentModels || {};
      const renderComponent = !!title;
      return (renderComponent && {
        ...title,
      }) || null;
    },
    upperOneUpStories() {
      const landingStoryConfig = {
        grid: 'single',
      };
      const headingOpts = {
        size: 'sm-standard',
        level: 'h2',
      };
      const headingStoriesOpts = {
        size: 'sm-standard',
        level: 'h3',
      };

      const { 'upper-one-up-stories': upperOneUpStories } = this.componentModels || {};
      const { promotions } = upperOneUpStories || {};
      const mediaOptions = {
        renderedSizes: {
          mobile: '100vw',
          desktop: '568px',
        },
      };

      const stories = (!!promotions?.length
      && transformPromotionToLandingStories(
        promotions,
        landingStoryConfig,
        null,
        headingOpts,
        null,
        headingStoriesOpts,
        mediaOptions,
      )) || [];

      const renderComponent = !!upperOneUpStories;
      return (
        (renderComponent && {
          stories,
        }) || null
      );
    },
    banner() {
      const { banner } = this.componentModels || {};
      const { media, content, bannerComponentProps } = banner || {};
      const renderComponent = !!banner;
      return (renderComponent && {
        media,
        content,
        bannerComponentProps,
      }) || null;
    },
    twoUpStories() {
      const { 'two-up-stories': twoUpStories } = this.componentModels || {};
      const renderComponent = !!twoUpStories;
      const stories = twoUpStories?.stories?.map((story) => {
        const storyCopy = JSON.parse(JSON.stringify(story));
        Object.assign(storyCopy?.media, {
          renderedSizes: {
            mobile: '100vw',
            desktop: '568px',
          },
        });
        return storyCopy;
      });
      return (renderComponent && {
        stories,
      }) || null;
    },
    lowerOneUpStories() {
      const landingStoryConfig = {
        grid: 'single',
      };
      const headingOpts = {
        size: 'sm-standard',
        level: 'h2',
      };
      const headingStoriesOpts = {
        size: 'sm-standard',
        level: 'h3',
      };

      const { 'lower-one-up-stories': lowerOneUpStories } = this.componentModels || {};
      const { promotions } = lowerOneUpStories || {};
      const mediaOptions = {
        renderedSizes: {
          mobile: '100vw',
          desktop: '568px',
        },
      };

      const stories = (!!promotions?.length
      && transformPromotionToLandingStories(
        promotions,
        landingStoryConfig,
        null,
        headingOpts,
        null,
        headingStoriesOpts,
        mediaOptions,
      )) || [];

      const renderComponent = !!lowerOneUpStories;
      return (
        (renderComponent && {
          stories,
        }) || null
      );
    },
    threeUpStories() {
      const { 'three-up-stories': threeUpStories } = this.componentModels || {};
      const renderComponent = !!threeUpStories;
      return (renderComponent
          && threeUpStories) || null;
    },
    longform() {
      const { longform } = this.componentModels || {};
      const renderComponent = !!longform;
      return (renderComponent && longform) || null;
    },
    faq() {
      const { faq } = this.componentModels || {};
      const renderComponent = !!faq;
      return (renderComponent && faq) || null;
    },
  },
  methods: {
    openModal(idx) {
      this.modalContent = this.threeUpStories?.stories[idx]?.modalContent;
      this.modalOpen = true;
    },
    closeModal() {
      this.modalOpen = false;
    },
  },
};
</script>
<template>
  <div
    class="about-rei"
    data-ui="about-rei"
  >
    <!-- Lead -->
    <section
      v-if="renderingMap.lead"
      :data-location-name="analyticsMap.lead"
      class="about-rei-page__lead"
      data-ui="about-rei-lead"
    >
      <lead-component
        :style="cssSurfacePaletteVars"
        v-bind="lead"
      />
    </section>
    <div class="about-rei__content-container">
      <!-- In Page Nav -->
      <in-page-nav
        v-if="renderingMap['in-page-nav']"
        :id="inPageNav.id"
        :data-location-name="analyticsMap['sticky-nav']"
        :nav-model="inPageNav.items"
        data-ui="about-rei-in-page-nav"
      />
      <div>
        <!-- Title -->
        <div
          v-if="renderingMap.title"
          :data-location-name="analyticsMap.title"
          class="about-rei__title-wrapper about-rei__container about-rei__container--narrow"
          data-ui="about-rei-title"
        >
          <h1 class="about-rei__title"> {{ title.text }} </h1>
        </div>

        <!-- Banner -->
        <section
          v-if="renderingMap['banner']"
          :data-location-name="analyticsMap.banner"
          data-ui="about-rei-banner"
          class="about-rei__container about-rei__container--narrow"
        >
          <landing-banner v-bind="banner.bannerComponentProps">
            <template
              #background
            >
              <landing-image-component
                v-bind="banner.media"
              />
            </template>
          </landing-banner>
        </section>

        <!-- Longform -->
        <section
          v-if="renderingMap['longform']"
          :data-location-name="analyticsMap['longform']"
          data-ui="about-rei-longform-columns"
          class="about-rei__container about-rei__container--narrow"
        >
          <longform-component
            v-for="(section, index) in longform.sections"
            :key="`subSection-${index + 1}`"
            :section="section"
            class="about-rei__longform"
          />
        </section>

        <!-- Upper-One-Up-Stories -->
        <section
          v-if="renderingMap['upper-one-up-stories']"
          :data-location-name="analyticsMap['upper-one-up-stories']"
          data-ui="about-rei-upper-one-up-stories"
          class="about-rei__container about-rei__container--narrow about-rei__upper-one-up-stories"
        >
          <stories-component v-bind="upperOneUpStories.stories" />
        </section>

        <!-- Youtube video player -->
        <section
          v-if="renderingMap['video-player']"
          :data-location-name="analyticsMap['video-player']"
          data-ui="about-rei-video"
          class="about-rei__container about-rei__container--narrow"
        >
          <div v-if="videoPlayer.stories.length">
            <div
              v-for="(video, index) in videoPlayer.stories"
              :key="`video-${index}`"
              class="about-rei__video"
            >
              <h2 class="one-up-story__heading">{{ video.heading }}</h2>
              <p>{{ video.body }}</p>
            </div>
          </div>
          <div class="about-rei__video">
            <you-tube-video-player
              :video-id="videoPlayer.videoId"
              :ambient-video-url="videoPlayer.ambientVideoUrl"
              :play-button-label="videoPlayer.playerButtonLabel"
              :ratio="videoPlayer.ratio"
            />
          </div>
        </section>

        <!-- Two-Up-Stories -->
        <section
          v-if="renderingMap['two-up-stories']"
          :data-location-name="analyticsMap['two-up-stories']"
          data-ui="about-rei-two-up-stories"
          class="about-rei__container about-rei__container--narrow"
        >
          <stories-component v-bind="twoUpStories" />
        </section>

        <!-- Three-Up-Stories -->
        <section
          v-if="renderingMap['three-up-stories']"
          :data-location-name="analyticsMap['three-up-stories']"
          data-ui="about-rei-three-up-stories"
          class="about-rei__container about-rei__container--narrow"
        >
          <h2 class="three-up-stories__header">{{ threeUpStories?.heading?.text }}</h2>
          <stories-component
            grid="triple"
          >
            <template #story>
              <story-component
                v-for="(story, idx) in threeUpStories.stories"
                :key="`story-${idx}`"
                :media="story.media"
                aspect-ratio="1/1"
              >
                <template #content>
                  <landing-content-block
                    v-bind="story.content"
                  >
                    <template #javascriptActions="{ javascriptActions }">
                      <landing-action
                        v-if="javascriptActions && javascriptActions[0]"
                        v-bind="javascriptActions[0]"
                        data-ui="open-modal-button"
                        @click="openModal(idx)"
                      />
                    </template>
                  </landing-content-block>
                </template>
              </story-component>
            </template>
          </stories-component>
        </section>

        <!-- Lower-One-Up-Stories -->
        <section
          v-if="renderingMap['lower-one-up-stories']"
          :data-location-name="analyticsMap['lower-one-up-stories']"
          data-ui="about-rei-lower-one-up-stories"
          class="about-rei__container about-rei__container--narrow"
        >
          <stories-component v-bind="lowerOneUpStories.stories" />
        </section>

        <!-- FAQ -->
        <section
          v-if="renderingMap['faq']"
          :data-location-name="analyticsMap['faq']"
          data-ui="about-rei-faq"
          class="about-rei__container about-rei__container--narrow"
        >
          <frequently-asked-questions
            :faq="faq"
          />
        </section>
      </div>
    </div>
    <!-- Modal -->
    <section class="about-rei-modal">
      <cdr-modal
        v-if="modalContent?.sections && modalContent?.heading?.text"
        :opened="modalOpen"
        label=""
        aria-describedby="description"
        @closed="closeModal"
      >
        <template #title>
          <cdr-text
            :tag="modalContent?.heading?.level"
            class="modal-header"
          >
            {{ modalContent?.heading?.text }}
          </cdr-text>
        </template>
        <longform-component
          v-for="(section, index) in modalContent.sections"
          :key="`section-${index + 1}`"
          :section="section"
        />
        <template #landing-action-icon>
          <icon-play-fill />
        </template>
      </cdr-modal>
    </section>
  </div>
</template>

<style lang="scss">
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';
.about-rei {

  &__content-container {
    @include cdr-container;

    @include cdr-sm-mq-up {
      display: grid;
      grid-template-columns: 1fr 4fr;
    }
  }

  &__container {
    margin-bottom: $cdr-space-three-x;

    @include cdr-sm-mq-up {
      margin-bottom: 7.2rem;
    }

    &--narrow {
      @include cdr-container;
      @include cdr-text-body-300;
    }

    &--no-padding {
      padding: 0;
    }

    h2 {
      @include cdr-text-heading-sans-600;
    }
  }

  &__title {
    @include cdr-text-heading-serif-1000;
    margin-top: $cdr-space-two-x;
  }

  // only target h3s added as html
  h3:not([class]) {
    @include cdr-text-body-strong-500;
    margin: 0 0 $cdr-space-one-x;
  }

  &__video {
    .youtube-player {
      display: flex;
      justify-content: center;
      align-items: center;
      &__wrapper {
        height: 0;
        max-width: 928px;
        padding-bottom: 56.25%;
        position: relative;
        width: 100%;
        @media (min-width: 928px) {
          height: 523px;
          padding-bottom: 0;
        }
        iframe {
          height: 100%;
          left: 0;
          position: absolute;
          top: 0;
          width: 100%;
        }
      }
      &__poster--hide {
        position: absolute;
        visibility: hidden;
      }
    }
  }

  &__upper-one-up-stories {
    .landing-image-component__image {
      object-fit: contain;
    }
  }
}
.modal {
  &-header {
    @include cdr-text-heading-serif-strong-700;
    font-weight: $cdr-text-strong-weight;
  }
  &-subheader {
    font-size: $cdr-text-default-size;
  }
}
</style>
